/*
 * Copyright (C) 2023 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, {useEffect} from 'react'
import {
  useMutation as baseUseMutation,
  useQuery as baseUseQuery,
  hashQueryKey,
  QueryClientProvider,
  QueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'
import type {
  UseQueryOptions,
  QueryKey,
  QueryFunction,
  UseMutationOptions,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import {PersistQueryClientProvider} from '@tanstack/react-query-persist-client'
import {createSyncStoragePersister} from '@tanstack/query-sync-storage-persister'
import wasPageReloaded from '@canvas/util/wasPageReloaded'
import {v4} from 'uuid'
import {useBroadcastWhenFetched, useReception} from './utils'

const CACHE_KEY = 'QUERY_CACHE'
const CHANNEL_KEY = 'QUERY_CHANNEL'
const oneDay = 1000 * 60 * 60 * 24
const commonQueryOptions = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  retry: false,
}

if (wasPageReloaded || localStorage.cacheBuster === undefined) {
  localStorage.cacheBuster = v4()
}

const cacheBuster: string = String(localStorage.cacheBuster)

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...commonQueryOptions,
      staleTime: oneDay,
      cacheTime: oneDay * 2,
    },
  },
})

export const persister = createSyncStoragePersister({
  key: CACHE_KEY,
  storage: sessionStorage,
})

export function QueryProvider({children}: {children: React.ReactNode}) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        dehydrateOptions: {
          shouldDehydrateQuery: query => {
            // don't persist cache on infinite queries
            if (query.options?.getNextPageParam) {
              return false
            }
            return query.state.status === 'success'
          },
        },
        buster: cacheBuster,
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}

export const queryClientWithoutPersist = new QueryClient({
  defaultOptions: {
    queries: {
      ...commonQueryOptions
    },
  }
})

export function QueryProviderWithoutPersist({children}: {children: React.ReactNode}) {
  return (
    <QueryClientProvider client={queryClientWithoutPersist}>
      {children}
    </QueryClientProvider>
  )
}

const queriesFetched = new Set<string>()

window.BroadcastChannel =
  window.BroadcastChannel ||
  class BroadcastChannel {
    close() {}

    postMessage() {}

    addEventListener() {}

    removeEventListener() {}
  }

const broadcastChannel = new BroadcastChannel(CHANNEL_KEY)

export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const ensureFetch = options.meta?.fetchAtLeastOnce || wasPageReloaded
  const hashedKey = hashQueryKey(options.queryKey || [])
  const wasAlreadyFetched = queriesFetched.has(hashedKey)

  const refetchOnMount = ensureFetch && !wasAlreadyFetched ? 'always' : options.refetchOnMount

  // Handle incoming broadcasts
  useReception({
    hashedKey,
    queryKey: options.queryKey,
    queryClient,
    channel: broadcastChannel,
    enabled: Boolean(options.meta?.broadcast),
  })

  const mergedOptions: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> = {
    ...options,
    refetchOnMount,
  }
  const queryResult = baseUseQuery<TQueryFnData, TError, TData, TQueryKey>(mergedOptions)

  if (
    queryResult.isFetchedAfterMount &&
    queryResult.fetchStatus === 'idle' &&
    queryResult.isSuccess
  ) {
    setTimeout(() => queriesFetched.add(hashedKey), 0)
  }

  useBroadcastWhenFetched({
    hashedKey,
    queryResult,
    channel: broadcastChannel,
    enabled: Boolean(options.meta?.broadcast),
  })

  return queryResult
}

export function useAllPages<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>) {
  const queryResult = useInfiniteQuery<TQueryFnData, TError, TData, TQueryKey>(options)

  useEffect(() => {
    if (queryResult.hasNextPage && !queryResult.isFetchingNextPage) {
      queryResult.fetchNextPage({
        cancelRefetch: false,
      })
    }
    // it's already exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.hasNextPage, queryResult.isFetchingNextPage, queryResult.fetchNextPage])

  return queryResult
}

export function useMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(options: UseMutationOptions<TData, TError, TVariables, TContext>) {
  return baseUseMutation<TData, TError, TVariables, TContext>(options)
}

export function prefetchQuery(queryKey: QueryKey, queryFn: QueryFunction) {
  const hashedKey = hashQueryKey(queryKey || [])
  const wasAlreadyFetched = queriesFetched.has(hashedKey)

  if (
    !wasAlreadyFetched &&
    !queryClient.getQueryData(queryKey) &&
    !queryClient.isFetching(queryKey)
  ) {
    queriesFetched.add(hashQueryKey(queryKey || []))
    queryClient.prefetchQuery({
      queryKey,
      queryFn,
    })
  }
}
